import { IcAlert, IcConfigInstall } from '@assets/icons/40x40';
import { IcFP, IcMap, IcMeasure, IcNeutralInfo, IcZone, IcZoneInfo } from '@assets/icons/40x40/mapMenu';
import { SidebarDynamicOptions } from '@components/map/MapContext';
import { groupBy } from 'lodash';

import { SEE_POI, Zone2DTypeEnum, Zone3DTypeEnum } from '@/types/config/config.types';
import { MapLayer } from '@/types/config/settings.types';
import { FilterMode } from '@/types/map.types';
import { MenuItemOption, MenuItemOptionIcons, MenuItemsIcons } from '@/types/menu.types';
import { PopupType, PopupTypeEnum } from '@/types/popup.types';

type MenuKey = keyof SidebarDynamicOptions | 'always';

export type MenuPopupType =
  | PopupTypeEnum.POI
  | PopupTypeEnum.FILTERS_MENU
  | PopupTypeEnum.DAILY_FLIGHT_PLAN
  | PopupTypeEnum.NEUTRAL_INFO
  | PopupTypeEnum.MAP_LAYER
  | PopupTypeEnum.STATISTIC_FILTER_MENU
  | PopupTypeEnum.ONGOING_DRONE_NOTIFICATIONS;

export interface MenuBuilderContext {
  mapLayers: MapLayer[];
  currentBaseLayerId: number;
  currentOverlays: MapLayer['id'][];
  isCreateMapLayerSelected: boolean;
  isCreatePoiSelected: boolean;
  handleBaseLayerChanges: (baseLayer: number) => void;
  handleOverlaysChanges: (overlays: number) => void;
  canCreateMapLayer: boolean;
  canAccessFlightPlans: boolean;
  canSeeNotifications: boolean;
  canAccessNeutral: boolean;
  filterMode: FilterMode;
  popups: PopupType[];
  toggleMeasureTool: () => void;
  handlePopupOpeningByType: (type: MenuPopupType) => void;
  isOpenedPopupByType: (popups: PopupType[], popup: PopupTypeEnum) => boolean;
  handleZoneCreateClick: (zoneType: Zone2DTypeEnum | Zone3DTypeEnum) => void;
}

interface MenuBuilder {
  key: MenuKey;
  order: number;
  build: (ctx: MenuBuilderContext) => [string, MenuItemOptionIcons];
}

const buildBaseLayerOptions = (ctx: MenuBuilderContext, baseLayers: MapLayer[]): Record<string, MenuItemOption> =>
  Object.fromEntries(
    baseLayers.map((baseLayer) => [
      baseLayer.code,
      {
        label: baseLayer.name,
        shouldNotTranslate: true,
        isSelected: ctx.currentBaseLayerId === baseLayer.id,
        onAction: () => ctx.handleBaseLayerChanges(baseLayer.id),
      },
    ]),
  );

const buildMeshOptions = (ctx: MenuBuilderContext, meshes: MapLayer[]): Record<string, MenuItemOption> =>
  Object.fromEntries(
    meshes.map((mesh) => [
      mesh.code,
      {
        label: mesh.name,
        shouldNotTranslate: true,
        isSelected: ctx.currentOverlays.includes(mesh.id),
        onAction: () => ctx.handleOverlaysChanges(mesh.id),
      },
    ]),
  );

const buildMapsMenu: MenuBuilder['build'] = (ctx): [string, MenuItemOptionIcons] => {
  const { BASE_LAYER: baseLayers = [], MESH: meshes = [] } = groupBy(ctx.mapLayers, 'type');

  return [
    'maps',
    {
      label: 'controls.toolMenu.maps.maps',
      icon: IcMap,
      subMenuItems: {
        baseLayers: {
          type: 'group',
          label: 'controls.toolMenu.maps.type.BASE_LAYER',
          items: buildBaseLayerOptions(ctx, baseLayers),
        },
        mesh: {
          type: 'group',
          label: 'controls.toolMenu.maps.type.MESH',
          items: buildMeshOptions(ctx, meshes),
        },
        createMapLayer: {
          label: 'controls.toolMenu.maps.createMapLayer',
          isHidden: !ctx.canCreateMapLayer,
          isSelected: ctx.isCreateMapLayerSelected,
          onAction: () => ctx.handlePopupOpeningByType(PopupTypeEnum.MAP_LAYER),
        },
      },
    },
  ];
};

const buildZonesMenu: MenuBuilder['build'] = (ctx): [string, MenuItemOptionIcons] => [
  'zones',
  {
    label: 'controls.toolMenu.zones.zones',
    icon: IcZone,
    isHidden: true, //TODO : remove in next ticket if zone edition not needed on map anymore
    subMenuItems: {
      danger: {
        label: 'controls.toolMenu.zones.dangerZone.title',
        subMenuItems: {
          observation: {
            label: 'controls.toolMenu.zones.dangerZone.observation',
            subMenuItems: {
              twoDimension: {
                label: 'controls.toolMenu.zones.twoDimension',
                subMenuItems: {
                  [`see_${Zone2DTypeEnum.NFZ_INFO}`]: {
                    label: 'controls.toolMenu.zones.interact.see',
                  },
                  create: {
                    label: 'controls.toolMenu.zones.interact.create',
                    onAction: () => ctx.handleZoneCreateClick(Zone2DTypeEnum.NFZ_INFO),
                  },
                },
              },
              threeDimension: {
                label: 'controls.toolMenu.zones.threeDimension',
                subMenuItems: {
                  [`see_${Zone3DTypeEnum.NFZ_INFO_3D}`]: {
                    label: 'controls.toolMenu.zones.interact.see',
                  },
                  create: {
                    label: 'controls.toolMenu.zones.interact.create',
                    isDisabled: true, // On n'a pas encore le formulaire de zones 3d
                  },
                },
              },
            },
          },
          vigilance: {
            label: 'controls.toolMenu.zones.dangerZone.vigilance',
            subMenuItems: {
              twoDimension: {
                label: 'controls.toolMenu.zones.twoDimension',
                subMenuItems: {
                  [`see_${Zone2DTypeEnum.NFZ_WARNING}`]: {
                    label: 'controls.toolMenu.zones.interact.see',
                  },
                  create: {
                    label: 'controls.toolMenu.zones.interact.create',
                    onAction: () => ctx.handleZoneCreateClick(Zone2DTypeEnum.NFZ_WARNING),
                  },
                },
              },
              threeDimension: {
                label: 'controls.toolMenu.zones.threeDimension',
                subMenuItems: {
                  [`see_${Zone3DTypeEnum.NFZ_WARNING_3D}`]: {
                    label: 'controls.toolMenu.zones.interact.see',
                  },
                  create: {
                    label: 'controls.toolMenu.zones.interact.create',
                    isDisabled: true, // On n'a pas encore le formulaire de zones 3d
                  },
                },
              },
            },
          },
          instantDanger: {
            label: 'controls.toolMenu.zones.dangerZone.instantDanger',
            subMenuItems: {
              twoDimension: {
                label: 'controls.toolMenu.zones.twoDimension',
                subMenuItems: {
                  [`see_${Zone2DTypeEnum.NFZ_CRITICAL}`]: {
                    label: 'controls.toolMenu.zones.interact.see',
                  },
                  create: {
                    label: 'controls.toolMenu.zones.interact.create',
                    onAction: () => ctx.handleZoneCreateClick(Zone2DTypeEnum.NFZ_CRITICAL),
                  },
                },
              },
              threeDimension: {
                label: 'controls.toolMenu.zones.threeDimension',
                subMenuItems: {
                  [`see_${Zone3DTypeEnum.NFZ_CRITICAL_3D}`]: {
                    label: 'controls.toolMenu.zones.interact.see',
                  },
                  create: {
                    label: 'controls.toolMenu.zones.interact.create',
                    isDisabled: true, // On n'a pas encore le formulaire de zones 3d
                  },
                },
              },
            },
          },
        },
      },
      system: {
        label: 'controls.toolMenu.zones.system.system',
        subMenuItems: {
          niz: {
            label: 'controls.toolMenu.zones.system.niz',
            subMenuItems: {
              [`see_${Zone2DTypeEnum.NIZ}`]: {
                label: 'controls.toolMenu.zones.interact.see',
              },
              create: {
                label: 'controls.toolMenu.zones.interact.create',
                onAction: () => ctx.handleZoneCreateClick(Zone2DTypeEnum.NIZ),
              },
            },
          },
          maskingZone: {
            label: 'controls.toolMenu.zones.system.maskingZone',
            subMenuItems: {
              [`see_${Zone2DTypeEnum.IMZ}`]: {
                label: 'controls.toolMenu.zones.interact.see',
              },
              create: {
                label: 'controls.toolMenu.zones.interact.create',
                onAction: () => ctx.handleZoneCreateClick(Zone2DTypeEnum.IMZ),
              },
            },
          },
          /*criticalSites: {
            label: 'controls.toolMenu.zones.system.criticalSites',
            children: {
              see: {
                label: 'controls.toolMenu.zones.interact.see',
              },
              create: {
                label: 'controls.toolMenu.zones.interact.create',
                //onAction: () => handleZoneCreateClick(undefined),
              },
            },
          },*/
        },
      },
      /*personalized: {
        label: 'controls.toolMenu.zones.personalized.personalized',
        children: {
          segments: {
            label: 'controls.toolMenu.zones.personalized.segments',
            children: {
              see: {
                label: 'controls.toolMenu.zones.interact.see',
              },
              create: {
                label: 'controls.toolMenu.zones.interact.create',
                //onAction: () => handleZoneCreateClick(undefined),
              },
            },
          },
          polygon: {
            label: 'controls.toolMenu.zones.personalized.polygon',
            children: {
              see: {
                label: 'controls.toolMenu.zones.interact.see',
              },
              create: {
                label: 'controls.toolMenu.zones.interact.create',
                //onAction: () => handleZoneCreateClick(undefined),
              },
            },
          },
          circle: {
            label: 'controls.toolMenu.zones.personalized.circle',
            children: {
              see: {
                label: 'controls.toolMenu.zones.interact.see',
              },
              create: {
                label: 'controls.toolMenu.zones.interact.create',
                //onAction: () => handleZoneCreateClick(undefined),
              },
            },
          },
        },
      },*/
    },
  },
];

const buildInfoMenu: MenuBuilder['build'] = (ctx): [string, MenuItemOptionIcons] => [
  'info',
  {
    label: 'controls.toolMenu.info.info',
    icon: IcZoneInfo,
    subMenuItems: {
      /*aeronautics: {
        label: 'controls.toolMenu.info.aeronautics',
      },*/
      zoi: {
        label: 'controls.toolMenu.info.zoi',
        isDisabled: true,
      },
      poi: {
        label: 'controls.toolMenu.info.poi',
        subMenuItems: {
          [SEE_POI]: {
            label: 'controls.toolMenu.info.seePoi',
          },
          createPoi: {
            label: 'controls.toolMenu.info.createPoi',
            isSelected: ctx.isCreatePoiSelected,
            onAction: () => ctx.handlePopupOpeningByType(PopupTypeEnum.POI),
          },
        },
      },
    },
  },
];

const buildFiltersMenu: MenuBuilder['build'] = (ctx): [string, MenuItemOptionIcons] => [
  'filters',
  {
    label: 'controls.toolMenu.filters',
    icon: IcConfigInstall,
    isActive:
      ctx.filterMode === 'regular'
        ? ctx.isOpenedPopupByType(ctx.popups, PopupTypeEnum.FILTERS_MENU)
        : ctx.isOpenedPopupByType(ctx.popups, PopupTypeEnum.STATISTIC_FILTER_MENU),
    onAction:
      ctx.filterMode === 'regular'
        ? () => ctx.handlePopupOpeningByType(PopupTypeEnum.FILTERS_MENU)
        : () => ctx.handlePopupOpeningByType(PopupTypeEnum.STATISTIC_FILTER_MENU),
  },
];

const buildDailyFlightPlansMenu: MenuBuilder['build'] = (ctx): [string, MenuItemOptionIcons] => [
  'dailyFlightPlans',
  {
    label: 'controls.toolMenu.dailyFlightPlans',
    icon: IcFP,
    isHidden: !ctx.canAccessFlightPlans,
    isActive: ctx.isOpenedPopupByType(ctx.popups, PopupTypeEnum.DAILY_FLIGHT_PLAN),
    onAction: () => ctx.handlePopupOpeningByType(PopupTypeEnum.DAILY_FLIGHT_PLAN),
  },
];

const buildShowNeutralInfoMenu: MenuBuilder['build'] = (ctx): [string, MenuItemOptionIcons] => [
  'neutralInfo',
  {
    label: 'controls.toolMenu.neutralInfo',
    icon: IcNeutralInfo,
    isHidden: !ctx.canAccessNeutral,
    isActive: ctx.isOpenedPopupByType(ctx.popups, PopupTypeEnum.NEUTRAL_INFO),
    onAction: () => ctx.handlePopupOpeningByType(PopupTypeEnum.NEUTRAL_INFO),
  },
];

const buildMeasureMenu: MenuBuilder['build'] = (ctx): [string, MenuItemOptionIcons] => [
  'measure',
  {
    label: 'controls.toolMenu.measure.measure',
    icon: IcMeasure,
    subMenuItems: {
      azimuthAndDistance: {
        label: 'controls.toolMenu.measure.azimuthAndDistance.label',
        onAction: () => ctx.toggleMeasureTool(),
      },
    },
  },
];

const buildOngoingDroneNotificationsMenu: MenuBuilder['build'] = (ctx): [string, MenuItemOptionIcons] => [
  'ongoingDroneNotifications',
  {
    label: 'controls.toolMenu.ongoingDroneNotifications',
    icon: IcAlert,
    isHidden: !ctx.canSeeNotifications,
    isActive: ctx.isOpenedPopupByType(ctx.popups, PopupTypeEnum.ONGOING_DRONE_NOTIFICATIONS),
    onAction: () => ctx.handlePopupOpeningByType(PopupTypeEnum.ONGOING_DRONE_NOTIFICATIONS),
  },
];

const menuBuilders: MenuBuilder[] = [
  {
    key: 'always',
    order: 1,
    build: buildMapsMenu,
  },
  {
    key: 'showZones',
    order: 2,
    build: buildZonesMenu,
  },
  {
    key: 'showInfo',
    order: 3,
    build: buildInfoMenu,
  },
  {
    key: 'showFilters',
    order: 4,
    build: buildFiltersMenu,
  },
  {
    key: 'showOngoingDroneNotifications',
    order: 5,
    build: buildOngoingDroneNotificationsMenu,
  },
  {
    key: 'showDailyFlightPlans',
    order: 6,
    build: buildDailyFlightPlansMenu,
  },
  {
    key: 'showNeutralInfo',
    order: 7,
    build: buildShowNeutralInfoMenu,
  },
  {
    key: 'always',
    order: 8,
    build: buildMeasureMenu,
  },
];

export const buildMenuItems = (options: SidebarDynamicOptions, ctx: MenuBuilderContext): MenuItemsIcons => {
  const items = menuBuilders
    .filter(({ key }) => key === 'always' || options[key])
    .sort((a, b) => a.order - b.order)
    .map(({ build }) => build(ctx));

  return Object.fromEntries(items);
};
